/**
 * Wallet top-up lifecycle: create order → Cashfree hosted checkout (full-page
 * redirect) → back on the wallet page → server-side verification on a backoff
 * → one final, honest result.
 *
 * Guarantees:
 *  - Never claims success from the redirect alone: only a server `credited`.
 *  - Never claims failure on a transport error: that is "couldn't confirm yet".
 *  - Never invites a second payment while the bank is still `processing`.
 *  - Retrying a declined / abandoned payment reuses the SAME order (Cashfree
 *    session) — no duplicate orders; a fresh order only when that is impossible.
 *  - A checkout the user leaves mid-way is remembered (localStorage) and
 *    re-checked on the next visit.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import walletApi, { type TopupStatus, walletErrorMessage } from '@/services/wallet.api';
import {
  clearPendingTopup,
  nextPollDelay,
  readPendingTopup,
  rememberPendingTopup,
} from '@/utils/walletTopupPolling';

declare global {
  interface Window {
    Cashfree?: (opts: { mode: 'sandbox' | 'production' }) => {
      checkout: (opts: { paymentSessionId: string; redirectTarget?: '_self' | '_blank' | '_modal' }) => Promise<void> | void;
    };
  }
}

const CASHFREE_ENV: 'sandbox' | 'production' =
  import.meta.env['VITE_CASHFREE_ENV'] === 'production' ? 'production' : 'sandbox';
const ORDER_ID_PARAM = 'order_id';

export type VerifyPhase =
  | { phase: 'idle' }
  | { phase: 'verifying'; gatewayOrderId: string; amount?: number | undefined; last?: TopupStatus | undefined }
  | { phase: 'done'; gatewayOrderId: string; status: TopupStatus; handedOff: boolean }
  | { phase: 'unknown'; gatewayOrderId: string; amount?: number | undefined; message: string };

let cashfreeScript: Promise<void> | null = null;
function loadCashfree(): Promise<void> {
  if (window.Cashfree) return Promise.resolve();
  if (cashfreeScript) return cashfreeScript;
  cashfreeScript = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://sdk.cashfree.com/js/v3/cashfree.js';
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      cashfreeScript = null;
      script.remove();
      reject(new Error('Could not load the secure payment page. Check your connection (or ad-blocker) and try again.'));
    };
    document.body.appendChild(script);
  });
  return cashfreeScript;
}

async function openCheckout(paymentSessionId: string): Promise<void> {
  await loadCashfree();
  if (!window.Cashfree) throw new Error('Could not start the secure payment page. Please try again.');
  await window.Cashfree({ mode: CASHFREE_ENV }).checkout({ paymentSessionId, redirectTarget: '_self' });
}

export interface UseWalletTopupOptions {
  /** Called once when a top-up is confirmed credited (refresh balances, history). */
  onCredited?: (status: TopupStatus) => void;
  /** Called on any final result (refresh the recent-orders list). */
  onSettled?: () => void;
}

export function useWalletTopup({ onCredited, onSettled }: UseWalletTopupOptions = {}) {
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);
  const [needsPhoneFor, setNeedsPhoneFor] = useState<number | null>(null);
  const [verify, setVerify] = useState<VerifyPhase>({ phase: 'idle' });
  const runId = useRef(0);
  const callbacks = useRef({ onCredited, onSettled });
  callbacks.current = { onCredited, onSettled };

  /** Poll the server until a final state or the state's patience budget runs out. */
  const verifyOrder = useCallback(async (gatewayOrderId: string, amount?: number) => {
    const myRun = ++runId.current;
    const startedAt = Date.now();
    setVerify({ phase: 'verifying', gatewayOrderId, amount });

    let attempt = 0;
    let last: TopupStatus | undefined;
    let lastError: string | undefined;
    for (;;) {
      if (runId.current !== myRun) return; // superseded / unmounted
      let state: TopupStatus['state'] | 'unknown';
      try {
        last = await walletApi.confirmTopup(gatewayOrderId);
        lastError = undefined;
        state = last.state;
        if (runId.current !== myRun) return;
        setVerify({ phase: 'verifying', gatewayOrderId, amount: last.amount, last });
      } catch (err: any) {
        if (err?.response?.status === 404) {
          clearPendingTopup(gatewayOrderId);
          setVerify({
            phase: 'unknown',
            gatewayOrderId,
            amount,
            message: 'We could not find this top-up order on your workspace.',
          });
          return;
        }
        state = 'unknown';
        lastError = walletErrorMessage(err, 'We could not reach the server.');
      }

      const delay = nextPollDelay(state, attempt, Date.now() - startedAt);
      if (delay === null) break;
      attempt += 1;
      await new Promise((r) => setTimeout(r, delay));
    }
    if (runId.current !== myRun) return;

    if (!last) {
      setVerify({
        phase: 'unknown',
        gatewayOrderId,
        amount,
        message: `${lastError ?? 'We could not confirm the payment right now.'} If you completed the payment, it will be added automatically — you don't need to pay again.`,
      });
      return;
    }

    const final = ['credited', 'failed', 'cancelled', 'expired'].includes(last.state);
    setVerify({ phase: 'done', gatewayOrderId, status: last, handedOff: !final });
    // Keep remembering orders that may still complete (processing / crediting).
    if (final || last.state === 'awaiting') clearPendingTopup(gatewayOrderId);
    if (last.state === 'credited') callbacks.current.onCredited?.(last);
    callbacks.current.onSettled?.();
  }, []);

  // Return from checkout (?order_id=…) or a remembered unfinished order.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const fromRedirect = params.get(ORDER_ID_PARAM);
    if (fromRedirect) {
      // Strip it at once so a refresh doesn't re-run the flow from the URL.
      params.delete(ORDER_ID_PARAM);
      const rest = params.toString();
      window.history.replaceState(window.history.state, '', window.location.pathname + (rest ? `?${rest}` : ''));
      const pending = readPendingTopup();
      void verifyOrder(fromRedirect, pending?.gatewayOrderId === fromRedirect ? pending.amount : undefined);
    } else {
      const pending = readPendingTopup();
      if (pending) void verifyOrder(pending.gatewayOrderId, pending.amount);
    }
    return () => {
      runId.current += 1; // cancels any running poll
    };
  }, [verifyOrder]);

  /** Start a NEW top-up. Phone is only sent after the PHONE_NUMBER_REQUIRED prompt. */
  const startTopup = useCallback(async (amount: number, phoneNumber?: string) => {
    if (starting) return; // double-click guard
    setStarting(true);
    setStartError(null);
    try {
      // Load the SDK first: if it's blocked we fail before creating an order.
      await loadCashfree();
      const order = await walletApi.createTopupOrder({ amount, ...(phoneNumber ? { phoneNumber } : {}) });
      if (!order.paymentSessionId) throw new Error('The payment could not be started. Please try again.');
      rememberPendingTopup({ gatewayOrderId: order.gatewayOrderId, amount: order.amount });
      await openCheckout(order.paymentSessionId);
      // '_self' navigates away; if we are still here the SDK returned without redirecting.
    } catch (err: any) {
      const message = err?.response?.data?.error?.message;
      if (message === 'PHONE_NUMBER_REQUIRED') {
        setNeedsPhoneFor(amount);
      } else {
        setStartError(walletErrorMessage(err, err?.message || 'Could not start the payment. Please try again.'));
      }
    } finally {
      setStarting(false);
    }
  }, [starting]);

  /** Retry after a decline / cancel: same order when possible, else a fresh one. */
  const retry = useCallback(
    async (status: TopupStatus) => {
      setVerify({ phase: 'idle' });
      if (status.canRetry && status.paymentSessionId) {
        setStarting(true);
        setStartError(null);
        try {
          rememberPendingTopup({ gatewayOrderId: status.gatewayOrderId, amount: status.amount });
          await openCheckout(status.paymentSessionId);
          return;
        } catch {
          // Session no longer usable — fall through to a new order.
        } finally {
          setStarting(false);
        }
      }
      await startTopup(status.amount);
    },
    [startTopup]
  );

  const dismiss = useCallback(() => {
    runId.current += 1;
    setVerify({ phase: 'idle' });
  }, []);

  return {
    starting,
    startError,
    clearStartError: () => setStartError(null),
    needsPhoneFor,
    cancelPhonePrompt: () => setNeedsPhoneFor(null),
    submitPhone: (phone: string) => {
      const amount = needsPhoneFor;
      setNeedsPhoneFor(null);
      if (amount !== null) void startTopup(amount, phone);
    },
    startTopup,
    verify,
    verifyOrder,
    retry,
    dismiss,
  };
}

/** Same normalisation the backend applies for Cashfree (10-digit Indian mobile). */
export function normalizeIndianMobile(raw: string): string | null {
  let digits = raw.replace(/[^\d]/g, '');
  if (digits.length === 12 && digits.startsWith('91')) digits = digits.slice(2);
  else if (digits.length === 11 && digits.startsWith('0')) digits = digits.slice(1);
  return /^[6-9]\d{9}$/.test(digits) ? digits : null;
}
